import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { 
  UserPlusIcon, 
  PencilIcon, 
  TrashIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  EllipsisVerticalIcon,
  FunnelIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

import MainLayout from '../components/Layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import { User, UserRole, UserStatus } from '../types/user';
import { userService, UserListResponse } from '../services/userService';
import useDebounce from '../hooks/useDebounce';
import useSubmissionProtection from '../hooks/useSubmissionProtection';
import { 
  Input, 
  Select, 
  Button, 
  Modal, 
  Textarea, 
  DataTable, 
  Badge, 
  showToast,
  ConfirmDialog
} from '../components/UI';

interface UserFormData {
  name: string;
  username: string;
  email: string;
  mobile: string;
  password?: string;
  address?: string;
  role: UserRole;
  status: UserStatus;
  isEdit?: boolean;
}

interface TableParams {
  page: number;
  limit: number;
  search: string;
  role: string;
  status: string;
}

// Validation schema
const userSchema = yup.object({
  name: yup.string().required('Name is required').min(2, 'Name must be at least 2 characters'),
  username: yup.string().required('Username is required').min(3, 'Username must be at least 3 characters'),
  email: yup.string().email('Invalid email format').optional(),
  mobile: yup.string().matches(/^[0-9]{10}$/, 'Mobile must be 10 digits').required('Mobile is required'),
  password: yup.string().when('isEdit', {
    is: false,
    then: (schema) => schema.required('Password is required').min(6, 'Password must be at least 6 characters'),
    otherwise: (schema) => schema.optional()
  }),
  address: yup.string().optional(),
  role: yup.mixed<UserRole>().oneOf(['ADMIN', 'MANAGER', 'FIELD_TEAM']).required('Role is required'),
  status: yup.mixed<UserStatus>().oneOf(['ACTIVE', 'INACTIVE']).required('Status is required'),
  isEdit: yup.boolean().optional(),
});

const UserManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showActionMenu, setShowActionMenu] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; user: User | null }>({
    show: false,
    user: null
  });
  const [deleting, setDeleting] = useState(false);
  
  // Pagination and filter states
  const [tableParams, setTableParams] = useState<TableParams>({
    page: 1,
    limit: 10,
    search: '',
    role: '',
    status: ''
  });
  
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Ref to prevent multiple API calls
  const loadingRef = useRef(false);
  
  // Submission protection hook
  const { isSubmitting: isProtectedSubmitting, protectedSubmit } = useSubmissionProtection({
    debounceTime: 2000,
    enableDataHashing: true,
    customHashFn: (data) => JSON.stringify({ ...data, editingUserId: editingUser?.id })
  });
  
  // Debounced search value
  const debouncedSearch = useDebounce(searchInput, 500);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setError
  } = useForm<UserFormData>({
    resolver: yupResolver(userSchema) as any,
    defaultValues: {
      name: '',
      username: '',
      email: '',
      password: '',
      mobile: '',
      address: '',
      role: 'FIELD_TEAM',
      status: 'ACTIVE',
      isEdit: false
    }
  });

  const roleOptions = [
    { value: 'ADMIN', label: 'Administrator' },
    { value: 'MANAGER', label: 'Manager' },
    { value: 'FIELD_TEAM', label: 'Field Team' }
  ];

  const statusOptions = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' }
  ];

  // Load users with pagination and filters
  const loadUsers = useCallback(async (params: TableParams, sortingState?: SortingState) => {
    // Prevent multiple concurrent calls
    if (loadingRef.current) return;
    loadingRef.current = true;
    
    try {
      setLoading(true);
      
      // Convert sorting state to API parameters
      let sortBy: string | undefined;
      let sortOrder: 'asc' | 'desc' | undefined;
      
      if (sortingState && sortingState.length > 0) {
        const sort = sortingState[0];
        sortBy = sort.id;
        sortOrder = sort.desc ? 'desc' : 'asc';
      }
      
      const response: UserListResponse = await userService.getUsers({
        page: params.page,
        limit: params.limit,
        search: params.search,
        role: params.role || undefined,
        status: params.status || undefined,
        sortBy,
        sortOrder
      });
      
      setUsers(response.data);
      setTotalUsers(response.pagination.total);
      setTotalPages(response.pagination.pages);
    } catch (error: any) {
      console.error('Error loading users:', error);
      showToast.error(error.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  // Effect for role-based access control - Admin only
  useEffect(() => {
    // Check if user has permission to access this page
    if (user && user.role !== 'ADMIN') {
      // Redirect to dashboard or show unauthorized message
      showToast.error('Only administrators can access User Management');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Effect for debounced search
  useEffect(() => {
    setTableParams(prev => ({
      ...prev,
      search: debouncedSearch,
      page: 1 // Reset to first page when searching
    }));
  }, [debouncedSearch]);

  // Load users when table params or sorting changes - only if Admin
  useEffect(() => {
    if (user && user.role === 'ADMIN') {
      loadUsers(tableParams, sorting);
    }
  }, [tableParams, sorting, user, loadUsers]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePageChange = (page: number) => {
    setTableParams(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (limit: number) => {
    setTableParams(prev => ({ ...prev, limit, page: 1 }));
  };

  const handleRoleFilter = (role: string) => {
    setTableParams(prev => ({ ...prev, role, page: 1 }));
  };

  const handleStatusFilter = (status: string) => {
    setTableParams(prev => ({ ...prev, status, page: 1 }));
  };

  const handleEdit = useCallback((user: User) => {
    reset({
      name: user.name,
      username: user.username,
      email: user.email,
      password: '',
      mobile: user.mobile,
      address: user.address || '',
      role: user.role,
      status: user.status,
      isEdit: true
    });
    setEditingUser(user);
    setShowModal(true);
  }, [reset]);

  const handleDelete = (user: User) => {
    setDeleteConfirm({ show: true, user });
    setShowActionMenu(null);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.user) return;
    
    try {
      setDeleting(true);
      await userService.deleteUser(deleteConfirm.user.id);
      showToast.success('User deleted successfully');
      setDeleteConfirm({ show: false, user: null });
      loadUsers(tableParams, sorting);
    } catch (error: any) {
      console.error('Error deleting user:', error);
      showToast.error(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    reset();
  };

  const onSubmit = async (data: UserFormData) => {
    const submitFunction = async (formData: UserFormData) => {
      setSubmitting(true);
      try {
        const { isEdit, password, ...userData } = formData;
        
        // Prepare the data based on whether it's create or update
        const requestData: any = { ...userData };
        
        if (editingUser) {
          // For update, only include password if it's not empty
          if (password && password.trim() !== '') {
            requestData.password = password;
          }
          await userService.updateUser(editingUser.id, requestData);
          showToast.success('User updated successfully');
        } else {
          // For create, password is required
          requestData.password = password;
          await userService.createUser(requestData);
          showToast.success('User created successfully');
        }
        
        // Close modal and reset form immediately
        handleCloseModal();
        loadUsers(tableParams, sorting);
      } finally {
        setSubmitting(false);
      }
    };

    try {
      await protectedSubmit(data, submitFunction);
    } catch (error: any) {
      console.error('Error saving user:', error);
      
      // Handle validation errors from backend
      if (error.response?.data?.errors) {
        const backendErrors = error.response.data.errors;
        Object.keys(backendErrors).forEach((field) => {
          setError(field as any, {
            type: 'server',
            message: backendErrors[field]
          });
        });
      } else {
        showToast.error(error.response?.data?.message || 'Failed to save user');
      }
      
      setSubmitting(false);
    }
  };

  const handleAddUser = () => {
    // Reset form first with empty values
    reset({
      name: '',
      username: '',
      email: '',
      password: '',
      mobile: '',
      address: '',
      role: 'FIELD_TEAM',
      status: 'ACTIVE',
      isEdit: false
    });
    setEditingUser(null);
    setShowModal(true);
  };

  const getRoleDisplayName = (role: UserRole) => {
    switch (role) {
      case 'ADMIN':
        return 'Administrator';
      case 'MANAGER':
        return 'Manager';
      case 'FIELD_TEAM':
        return 'Field Team';
      default:
        return role;
    }
  };

  const columns = useMemo<ColumnDef<User>[]>(() => [
    {
      id: 'sno',
      header: 'S.No',
      cell: ({ row }) => {
        // Calculate serial number based on current page and row index
        const serialNumber = (tableParams.page - 1) * tableParams.limit + row.index + 1;
        return (
          <div className="text-sm font-medium text-gray-600 text-center">
            {serialNumber}
          </div>
        );
      },
      size: 60,
      enableSorting: false,
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-medium mr-3">
            {row.original.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900">{row.original.name}</div>
            <div className="text-sm text-gray-500">@{row.original.username}</div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'email',
      header: 'Contact',
      cell: ({ row }) => (
        <div className="space-y-1">
          <div className="flex items-center text-sm text-gray-900">
            <EnvelopeIcon className="w-4 h-4 mr-2 text-gray-400" />
            {row.original.email}
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <PhoneIcon className="w-4 h-4 mr-2 text-gray-400" />
            {row.original.mobile}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'role',
      header: 'Role',
      cell: ({ row }) => (
        <Badge variant="primary" size="sm">
          <UserIcon className="w-3 h-3 mr-1" />
          {getRoleDisplayName(row.original.role)}
        </Badge>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge 
          variant={row.original.status === 'ACTIVE' ? 'success' : 'error'}
          size="sm"
        >
          <div className={`w-2 h-2 rounded-full mr-2 ${
            row.original.status === 'ACTIVE' ? 'bg-green-500' : 'bg-red-500'
          }`} />
          {row.original.status}
        </Badge>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => (
        <div className="text-sm text-gray-500">
          {new Date(row.original.createdAt).toLocaleDateString()}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex space-x-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleEdit(row.original)}
          >
            <PencilIcon className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleDelete(row.original)}
          >
            <TrashIcon className="w-4 h-4 text-red-500" />
          </Button>
        </div>
      ),
    },
  ], [tableParams.page, tableParams.limit, handleEdit]);

  // Check for authorization before rendering - Admin only
  if (user && user.role !== 'ADMIN') {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
            <div className="text-6xl mb-4">👥</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Admin Access Only</h2>
            <p className="text-gray-600 mb-4">You do not have permission to access this page.</p>
            <p className="text-sm text-gray-500">Only Administrator can manage users and their permissions.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex flex-col h-full gap-4 animate-fade-in">
        {/* Header */}
        <div className="flex-shrink-0 flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">User Management</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
              Manage system users and their permissions
              {totalUsers > 0 && (
                <span className="ml-2 text-sm text-gray-500">
                  ({totalUsers} total users)
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <Button
              onClick={handleAddUser}
              size="lg"
              className="w-full sm:w-auto"
              leftIcon={<UserPlusIcon className="w-5 h-5" />}
            >
              Add User
            </Button>
            {/* Mobile Filter Toggle Button */}
            {isMobile && (
              <div className="relative w-full sm:w-auto">
                <Button
                  onClick={() => setShowMobileFilters(!showMobileFilters)}
                  size="lg"
                  variant={showMobileFilters ? 'primary' : 'outline'}
                  className="w-full"
                  leftIcon={showMobileFilters ? <XMarkIcon className="w-5 h-5" /> : <FunnelIcon className="w-5 h-5" />}
                >
                  {showMobileFilters ? 'Close Filters' : 'Filters'}
                </Button>
                {/* Show indicator if filters are active */}
                {(tableParams.role || tableParams.status || tableParams.search) && !showMobileFilters && (
                  <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse"></span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Filters and Search - Always visible on desktop, toggleable on mobile */}
          {(!isMobile || showMobileFilters) && (
            <div className="flex-shrink-0 overflow-hidden">
              <div className="bg-white p-4 rounded-2xl shadow-medium border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="md:col-span-2">
                    <Input
                      placeholder="Search users by name, email, username, or mobile..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </div>
                  <Select
                    placeholder="Filter by role"
                    options={[{ value: '', label: 'All Roles' }, ...roleOptions]}
                    value={roleOptions.find(opt => opt.value === tableParams.role) || { value: '', label: 'All Roles' }}
                    onChange={(option: any) => handleRoleFilter(option?.value || '')}
                    isSearchable={true}
                    isClearable={true}
                  />
                  <Select
                    placeholder="Filter by status"
                    options={[{ value: '', label: 'All Status' }, ...statusOptions]}
                    value={statusOptions.find(opt => opt.value === tableParams.status) || { value: '', label: 'All Status' }}
                    onChange={(option: any) => handleStatusFilter(option?.value || '')}
                    isSearchable={true}
                    isClearable={true}
                  />
                </div>
                {/* Mobile close hint */}
                {isMobile && (
                  <div className="mt-3 text-center">
                    <button
                      onClick={() => setShowMobileFilters(false)}
                      className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      Tap filter button above to close
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

        {/* Data Table for Desktop */}
        {!isMobile ? (
          <div className="flex-1 min-h-0 bg-white rounded-2xl shadow-medium border border-gray-200 flex flex-col">
            <DataTable
              data={users}
              columns={columns}
              loading={loading}
              searchable={false} // We're handling search separately
              pageSize={tableParams.limit}
              showPagination={false} // Disable built-in pagination
              sorting={sorting}
              onSortingChange={setSorting}
            />
            
            {/* Custom Pagination */}
            <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-t border-gray-200 bg-white">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="text-sm text-gray-700 whitespace-nowrap">
                  Showing {((tableParams.page - 1) * tableParams.limit) + 1} to{' '}
                  {Math.min(tableParams.page * tableParams.limit, totalUsers)} of{' '}
                  {totalUsers} results
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
                  <div className="w-full sm:w-auto">
                    <Select
                      options={[
                        { value: '5', label: '5 per page' },
                        { value: '10', label: '10 per page' },
                        { value: '20', label: '20 per page' },
                        { value: '50', label: '50 per page' }
                      ]}
                      value={{ value: tableParams.limit.toString(), label: `${tableParams.limit} per page` }}
                      onChange={(option: any) => handlePageSizeChange(Number(option?.value))}
                      usePortal={false}
                      menuPlacement="top"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePageChange(tableParams.page - 1)}
                      disabled={tableParams.page === 1}
                    >
                      Previous
                    </Button>
                    <div className="flex items-center px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg min-w-[120px] justify-center">
                      Page {tableParams.page} of {totalPages}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePageChange(tableParams.page + 1)}
                      disabled={tableParams.page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Mobile Cards View
          <div className="flex-1 min-h-0 flex flex-col gap-4 relative">
            {loading && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 rounded-2xl">
                <div className="flex flex-col items-center p-6 bg-white rounded-2xl shadow-strong border border-gray-200">
                  <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                  <p className="text-gray-600 text-sm font-medium mt-3">Loading users...</p>
                </div>
              </div>
            )}
            <div className="flex-1 min-h-0 overflow-auto bg-white rounded-2xl shadow-medium border border-gray-200 p-4">
                <div className="space-y-3">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="bg-white rounded-2xl shadow-medium border border-gray-200 p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-medium">
                          {user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">@{user.username}</div>
                          <div className="mt-1">
                            <Badge 
                              variant={user.status === 'ACTIVE' ? 'success' : 'error'} 
                              size="sm"
                            >
                              {user.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      
                      <div className="relative">
                        <button
                          onClick={() => setShowActionMenu(showActionMenu === user.id ? null : user.id)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <EllipsisVerticalIcon className="w-5 h-5 text-gray-500" />
                        </button>
                        
                        {showActionMenu === user.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                            <button
                              onClick={() => {
                                handleEdit(user);
                                setShowActionMenu(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                            >
                              <PencilIcon className="w-4 h-4 mr-2" />
                              Edit
                            </button>
                            <button
                              onClick={() => {
                                handleDelete(user);
                                setShowActionMenu(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                            >
                              <TrashIcon className="w-4 h-4 mr-2" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <EnvelopeIcon className="w-4 h-4 mr-2 text-gray-400" />
                        {user.email}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <PhoneIcon className="w-4 h-4 mr-2 text-gray-400" />
                        {user.mobile}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <UserIcon className="w-4 h-4 mr-2 text-gray-400" />
                        {getRoleDisplayName(user.role)}
                      </div>
                    </div>
                  </div>
                ))}
                </div>
            </div>
            
            {/* Mobile Pagination */}
            <div className="flex-shrink-0 bg-white rounded-2xl p-4 shadow-medium border border-gray-200">
              <div className="flex flex-col space-y-3">
                <div className="text-sm text-center text-gray-700">
                  Page {tableParams.page} of {totalPages} ({totalUsers} users)
                </div>
                <div className="flex justify-center space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(tableParams.page - 1)}
                    disabled={tableParams.page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(tableParams.page + 1)}
                    disabled={tableParams.page === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit User Modal */}
        <Modal
          isOpen={showModal}
          onClose={handleCloseModal}
          title={editingUser ? 'Edit User Information' : 'Add New User'}
          subtitle="Updating user details will receive a privacy audit."
          size="xl"
        >
          <form onSubmit={handleSubmit(onSubmit as any)} noValidate className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label="Full Name *"
                    placeholder="Enter full name"
                    error={errors.name?.message}
                    autoFocus
                  />
                )}
              />

              <Controller
                name="username"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label="Username *"
                    placeholder="Enter username"
                    error={errors.username?.message}
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="email"
                    label="Email Address"
                    placeholder="Enter email address (optional)"
                    error={errors.email?.message}
                  />
                )}
              />

              <Controller
                name="mobile"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="tel"
                    label="Mobile Number *"
                    placeholder="Enter mobile number"
                    error={errors.mobile?.message}
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="password"
                    label={editingUser ? "New Password (optional)" : "Password *"}
                    placeholder={editingUser ? "Leave blank to keep current password" : "Enter password"}
                    error={errors.password?.message}
                    showPasswordToggle={true}
                  />
                )}
              />

              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={roleOptions.find(option => option.value === field.value)}
                    onChange={(option: any) => field.onChange(option?.value)}
                    label="Role *"
                    options={roleOptions}
                    error={errors.role?.message}
                    isSearchable={true}
                    isClearable={true}
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    label="Address"
                    placeholder="Enter address"
                    rows={3}
                    error={errors.address?.message}
                  />
                )}
              />

              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={statusOptions.find(option => option.value === field.value)}
                    onChange={(option: any) => field.onChange(option?.value)}
                    label="Status *"
                    options={statusOptions}
                    error={errors.status?.message}
                    isSearchable={true}
                    isClearable={true}
                  />
                )}
              />
            </div>

            <div className="flex justify-end pt-6">
              <Button
                type="submit"
                loading={submitting || isProtectedSubmitting}
                disabled={submitting || isProtectedSubmitting}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium"
              >
                Submit
              </Button>
            </div>
          </form>
        </Modal>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={deleteConfirm.show}
          onClose={() => setDeleteConfirm({ show: false, user: null })}
          onConfirm={confirmDelete}
          title="Delete User"
          message={`Are you sure you want to delete ${deleteConfirm.user?.name}? This action cannot be undone.`}
          confirmText="Delete User"
          cancelText="Cancel"
          variant="danger"
          loading={deleting}
        />
      </div>
    </MainLayout>
  );
};

export default UserManagement;